import React, { useState } from 'react';
import {
  X,
  FileSignature,
  FileType,
  ClipboardList,
  Upload,
  Download,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import {
  DocumentTemplate,
  FormField,
  CATEGORY_LABELS,
  staffDocumentUploads,
} from '../../services/staffDocumentService';
import SearchableDropdown from '../SearchableDropdown';
import DatePicker from '../DatePicker';

// Port of platform-app components/team/TemplateViewModal.tsx for the
// tenant-less staff-service (RUBY light chrome). `tenantId` is accepted for
// prop-contract compatibility but ignored.

// Keep in sync with TEMPLATE_TYPE_LABEL / TEMPLATE_TYPE_META in templateTypeMeta.ts
const TYPE_LABEL: Record<string, string> = {
  upload: 'Upload',
  acknowledgment: 'Acknowledgment',
  form: 'Web Form',
  sheet: 'Sheet',
  fillable_doc: 'Fillable Doc',
};

// Header icon tile uses light tints that read on the light header.
const HEADER_TILE: Record<string, string> = {
  acknowledgment: 'bg-rose-100 text-rose-700',
  fillable_doc: 'bg-amber-100 text-amber-700',
  form: 'bg-blue-100 text-blue-700',
  sheet: 'bg-teal-100 text-teal-700',
  upload: 'bg-stone-200 text-stone-700',
};

interface TemplateViewModalProps {
  template: DocumentTemplate;
  onClose: () => void;
  onEdit: () => void;
  /** Ignored (tenant-less staff-service); kept so page ports stay mechanical. */
  tenantId?: string;
}

const HeaderIcon: React.FC<{ type: string }> = ({ type }) => {
  const cls = 'w-5 h-5';
  if (type === 'acknowledgment') return <FileSignature className={cls} />;
  if (type === 'fillable_doc') return <FileType className={cls} />;
  if (type === 'form') return <ClipboardList className={cls} />;
  return <Upload className={cls} />;
};

const INPUT_CLS =
  'w-full px-3 py-2 border border-stone-300 rounded-lg text-stone-900 bg-white placeholder-stone-400 focus:border-admin-primary focus:outline-none';

const TemplateViewModal: React.FC<TemplateViewModalProps> = ({
  template,
  onClose,
  onEdit,
}) => {
  const type = template.templateType || 'upload';
  const typeLabel = TYPE_LABEL[type] || 'Upload';
  const headerTile = HEADER_TILE[type] || HEADER_TILE.upload;
  const categoryLabel = CATEGORY_LABELS[template.category] || 'Other';

  // ----- Acknowledgment local state (preview only) -----
  const [ackChecked, setAckChecked] = useState(false);
  const [ackSignature, setAckSignature] = useState('');
  const [ackDone, setAckDone] = useState(false);
  // Acknowledgments are signed in this product (mirrors the "Signature required" rule).
  const signatureRequired = true;
  const ackCanSubmit = ackChecked && (!signatureRequired || ackSignature.trim().length > 0);

  // ----- Form local state (preview only) -----
  const [formAnswers, setFormAnswers] = useState<Record<string, any>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formDone, setFormDone] = useState(false);

  // ----- Fillable / Upload local state (preview only) -----
  const [chosenFileName, setChosenFileName] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const sortedFields: FormField[] = template.formFields
    ? [...template.formFields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const setAnswer = (id: string, value: any) => {
    setFormAnswers((prev) => ({ ...prev, [id]: value }));
    setFormDone(false);
    if (formErrors[id]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleFormSubmit = () => {
    const errors: Record<string, string> = {};
    sortedFields.forEach((field) => {
      if (!field.required) return;
      const v = formAnswers[field.id];
      const empty =
        field.type === 'checkbox'
          ? !v
          : v === undefined || v === null || String(v).trim() === '';
      if (empty) errors[field.id] = 'This field is required';
    });
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      setFormDone(true);
    }
  };

  const handleAckSubmit = () => {
    if (!ackCanSubmit) return;
    setAckDone(true);
  };

  const handleDownloadBlank = async () => {
    if (!template.referenceFileKey) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await staffDocumentUploads.getDownloadUrl(
        template.referenceFileKey,
        template.referenceFileName,
      );
      if (res?.url) window.open(res.url, '_blank');
    } catch (e) {
      setDownloadError('Could not get download link.');
    } finally {
      setDownloading(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = formAnswers[field.id];
    const err = formErrors[field.id];
    const fType = field.type as string; // portal also renders 'number'

    return (
      <div key={field.id}>
        <label className="block text-sm font-medium text-stone-700 mb-1">
          {field.label}
          {field.required && <span className="text-red-600 ml-1">*</span>}
        </label>

        {fType === 'text' && (
          <input
            type="text"
            placeholder={field.placeholder || ''}
            value={value || ''}
            onChange={(e) => setAnswer(field.id, e.target.value)}
            className={INPUT_CLS}
          />
        )}

        {fType === 'textarea' && (
          <textarea
            rows={3}
            placeholder={field.placeholder || ''}
            value={value || ''}
            onChange={(e) => setAnswer(field.id, e.target.value)}
            className={`${INPUT_CLS} resize-none`}
          />
        )}

        {fType === 'number' && (
          <input
            type="number"
            placeholder={field.placeholder || ''}
            value={value ?? ''}
            onChange={(e) => setAnswer(field.id, e.target.value)}
            className={INPUT_CLS}
          />
        )}

        {fType === 'email' && (
          <input
            type="email"
            placeholder={field.placeholder || 'email@example.com'}
            value={value || ''}
            onChange={(e) => setAnswer(field.id, e.target.value)}
            className={INPUT_CLS}
          />
        )}

        {fType === 'phone' && (
          <input
            type="tel"
            placeholder={field.placeholder || '(555) 555-5555'}
            value={value || ''}
            onChange={(e) => setAnswer(field.id, e.target.value)}
            className={INPUT_CLS}
          />
        )}

        {fType === 'date' && (
          <DatePicker
            value={value || ''}
            onChange={(v) => setAnswer(field.id, v)}
            placeholder={field.placeholder || 'Select date'}
          />
        )}

        {fType === 'select' && (
          <SearchableDropdown
            options={(field.options || []).map((opt) => ({ value: opt, label: opt }))}
            value={value || null}
            onChange={(v) => setAnswer(field.id, v)}
            placeholder={field.placeholder || 'Select an option...'}
          />
        )}

        {fType === 'checkbox' && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => setAnswer(field.id, e.target.checked)}
              className="w-4 h-4 rounded accent-admin-primary"
            />
            <span className="text-sm text-stone-700">{field.placeholder || 'Yes'}</span>
          </label>
        )}

        {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl border border-stone-200 w-full max-w-md max-h-[90vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-stone-50 border-b border-stone-200 px-5 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${headerTile}`}>
              <HeaderIcon type={type} />
            </div>
            <div className="min-w-0">
              <h3 className="text-stone-900 text-sm font-semibold truncate">{template.name}</h3>
              <p className="text-[11px] text-stone-500 truncate">
                {typeLabel} · {categoryLabel}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-700 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Live preview pill */}
        <div className="px-5 pt-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Live preview — how staff members see it
          </span>
        </div>

        {/* Body — interactive staff member experience */}
        <div className="p-5 space-y-4">
          {template.description && (
            <p className="text-sm text-stone-600">{template.description}</p>
          )}

          {/* ---------- ACKNOWLEDGMENT ---------- */}
          {type === 'acknowledgment' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <h4 className="font-medium text-amber-800 mb-2">Please Read Carefully</h4>
                    <div className="text-sm text-amber-800/90 whitespace-pre-wrap">
                      {template.disclaimerText?.trim() ||
                        'By acknowledging this document, you confirm that you have read and understood its contents and agree to comply with its terms.'}
                    </div>
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
                <input
                  type="checkbox"
                  checked={ackChecked}
                  onChange={(e) => {
                    setAckChecked(e.target.checked);
                    setAckDone(false);
                  }}
                  className="mt-1 w-5 h-5 rounded accent-admin-primary"
                />
                <span className="text-sm text-stone-700">
                  I have read and understood the above, and I agree to its terms and conditions.
                </span>
              </label>

              {signatureRequired && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Type your full name to sign
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Jane Smith"
                    value={ackSignature}
                    onChange={(e) => {
                      setAckSignature(e.target.value);
                      setAckDone(false);
                    }}
                    className={INPUT_CLS}
                  />
                </div>
              )}

              {ackDone ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-100 border border-emerald-200 text-emerald-800 text-sm font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Acknowledged (preview)
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleAckSubmit}
                  disabled={!ackCanSubmit}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-admin-primary hover:bg-admin-secondary text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileSignature className="w-4 h-4" />
                  Acknowledge &amp; Sign
                </button>
              )}
            </div>
          )}

          {/* ---------- FORM ---------- */}
          {type === 'form' && (
            <div className="space-y-4">
              {sortedFields.length > 0 ? (
                sortedFields.map(renderField)
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No form fields configured</p>
                </div>
              )}

              {sortedFields.length > 0 &&
                (formDone ? (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-100 border border-emerald-200 text-emerald-800 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Submitted (preview)
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleFormSubmit}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-admin-primary hover:bg-admin-secondary text-white text-sm font-semibold transition-colors"
                  >
                    Submit
                  </button>
                ))}
            </div>
          )}

          {/* ---------- FILLABLE DOC ---------- */}
          {type === 'fillable_doc' && (
            <div className="space-y-4">
              <p className="text-xs text-stone-500">
                Staff members download this blank, fill it, and upload the completed copy.
              </p>

              <button
                type="button"
                onClick={handleDownloadBlank}
                disabled={!template.referenceFileKey || downloading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                {downloading
                  ? 'Preparing…'
                  : template.referenceFileKey
                  ? 'Download blank form'
                  : `Download blank${template.referenceFileName ? ` (${template.referenceFileName})` : ''}`}
              </button>

              {downloadError && <p className="text-xs text-red-600">{downloadError}</p>}

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Upload completed
                </label>
                <input
                  type="file"
                  onChange={(e) =>
                    setChosenFileName(e.target.files?.[0]?.name ?? null)
                  }
                  className="block w-full text-sm text-stone-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200"
                />
                {chosenFileName && (
                  <p className="text-xs text-stone-500 mt-1">
                    Selected: <span className="font-medium text-stone-700">{chosenFileName}</span> (preview only)
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ---------- UPLOAD (and sheet) ---------- */}
          {(type === 'upload' || type === 'sheet') && (
            <div className="space-y-3">
              {template.acceptedTypes && template.acceptedTypes.length > 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-700 mb-2">Accepted file types:</p>
                  <div className="flex flex-wrap gap-2">
                    {template.acceptedTypes.map((t) => (
                      <span
                        key={t}
                        className="px-2.5 py-1 rounded-full text-[11px] font-medium border border-blue-200 bg-white text-blue-700 uppercase"
                      >
                        {t.split('/')[1] || t}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-stone-500 italic">Any document type.</p>
              )}

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Upload document
                </label>
                <input
                  type="file"
                  onChange={(e) =>
                    setChosenFileName(e.target.files?.[0]?.name ?? null)
                  }
                  className="block w-full text-sm text-stone-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200"
                />
                {chosenFileName && (
                  <p className="text-xs text-stone-500 mt-1">
                    Selected: <span className="font-medium text-stone-700">{chosenFileName}</span> (preview only)
                  </p>
                )}
              </div>

              {template.hasExpiry && (
                <p className="text-[11px] text-stone-500">Requires an expiry date.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-stone-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-stone-500 hover:text-stone-900 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-admin-primary hover:bg-admin-secondary text-white text-sm font-semibold transition-colors"
          >
            <Edit2Icon />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

// Small inline edit icon to avoid an extra import collision; require-free SVG.
const Edit2Icon: React.FC = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

export default TemplateViewModal;
